// React 웹앱 측 - Step Counter Hook (권한 연동 개선)
import { useState, useEffect, useCallback, useRef } from 'react';

const detectEnvironment = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // 더 강력한 Android 감지
  const isAndroidUA = /Android/i.test(userAgent);
  const isAndroidNavigator = navigator.platform === 'Linux armv7l' || navigator.platform === 'Linux aarch64';
  const isAndroidAny = isAndroidUA || isAndroidNavigator || userAgent.includes('wv'); // WebView 감지

  console.log('🔍 Android 감지 결과:', {
    userAgent,
    isAndroidUA,
    isAndroidNavigator,
    isAndroidAny,
    platform: navigator.platform
  });

  return {
    isReactNativeWebView: !!window.ReactNativeWebView,
    isAndroid: isAndroidAny,
    isIOS: /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream,
    platform: isAndroidAny ? 'android' : (/iPad|iPhone|iPod/.test(userAgent) ? 'ios' : 'web'),
    userAgent
  };
};

export const useWebStepCounter = () => {
  const [currentSteps, setCurrentSteps] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [sensorType, setSensorType] = useState('none');
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  // 🔐 권한 관련 상태
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(false);
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);


  // ✅ 서버 동기화 관련 상태 추가
  const [syncStatus, setSyncStatus] = useState({
    lastSyncTime: 0,
    hasPendingSync: false,
    pendingCount: 0,
    isSyncing: false
  });
  const [weeklyData, setWeeklyData] = useState([]);

  // 자동 추적 시작을 위한 ref
  const shouldAutoStart = useRef(false);
  const permissionJustGranted = useRef(false);

  const [env] = useState(() => detectEnvironment());

  // React Native로 메시지 전송
  const sendToNative = useCallback((type, payload = {}) => {
    if (window.ReactNativeWebView) {
      console.log('📤 Native로 전송:', type, payload);
      window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    } else {
      console.warn('React Native WebView not available');
    }
  }, []);

  // 수동 동기화 요청
  const requestManualSync = useCallback(() => {
    if (env.isAndroid && hasPermission) {
      setSyncStatus(prev => ({ ...prev, isSyncing: true }));
      sendToNative('STEP_MANUAL_SYNC');
    }
  }, [sendToNative, hasPermission]);

  // 서버에서 일주일 데이터 요청
  const requestWeeklyDataFromServer = useCallback(() => {
    if (env.isAndroid && hasPermission) {
      sendToNative('STEP_GET_WEEKLY_SERVER');
    }
  }, [sendToNative, hasPermission]);

  // 동기화 상태 확인
  const checkSyncStatus = useCallback(() => {
    if (env.isAndroid && hasPermission) {
      sendToNative('STEP_GET_SYNC_STATUS');
    }
  }, [sendToNative, hasPermission]);


  // 🔐 권한 요청 함수 (자동 추적 시작 포함)
  const requestPermission = useCallback(async () => {
    console.log('🔐 걸음 수 권한 요청 시작...');
    setPermissionLoading(true);
    setError(null);
    shouldAutoStart.current = true; // 권한 받으면 자동 시작
    sendToNative('STEP_REQUEST_PERMISSION');
  }, [sendToNative]);

  // 🔐 권한 상태 확인
  const checkPermission = useCallback(() => {
    if (!permissionChecked) {
      console.log('🔍 권한 상태 확인 요청');
      sendToNative('STEP_CHECK_PERMISSION');
    }
  }, [sendToNative, permissionChecked]);

  // 걸음 수 추적 시작
  const startTracking = useCallback(() => {
    if (!hasPermission) {
      setError('먼저 걸음 수 측정 권한을 허용해주세요.');
      return false;
    }
    console.log('🚀 걸음 수 추적 시작 요청');
    sendToNative('STEP_START_TRACKING');
    return true;
  }, [sendToNative, hasPermission]);

  // 걸음 수 추적 중지
  const stopTracking = useCallback(() => {
    console.log('⏸️ 걸음 수 추적 중지 요청');
    sendToNative('STEP_STOP_TRACKING');
  }, [sendToNative]);

  // 현재 걸음 수 가져오기
  const getCurrentSteps = useCallback(() => {
    if (hasPermission) {
      // alert('test');
      sendToNative('STEP_GET_CURRENT');
    }
  }, [sendToNative, hasPermission]);

  // 센서 타입 가져오기
  const getSensorType = useCallback(() => {
    if (hasPermission) {
      sendToNative('STEP_GET_SENSOR_TYPE');
    }
  }, [sendToNative, hasPermission]);

  // 센서 가용성 확인
  const checkSensorAvailability = useCallback(() => {
    sendToNative('STEP_CHECK_SENSOR_AVAILABILITY');
  }, [sendToNative]);

  // React Native에서 온 메시지 처리
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const data = typeof event.data === 'string'
          ? JSON.parse(event.data)
          : event.data;

        const type = data.type;

        // alert(`dd:${type}`);

        switch (type) {
          case 'ready':
            setIsReady(true);
            console.log('✅ React Native bridge ready');
            break;

          case 'MIDNIGHT_RESET':
            console.log('Android 자정 리셋 수신:', data.newDate);
            setCurrentSteps(0);
            setError(null);
            window.dispatchEvent(new CustomEvent('stepData', {
              detail: {
                type: 'MIDNIGHT_RESET',
                dateChanged: true,
                triggerType: 'MIDNIGHT_RESET',
                newDate: data.newDate
              }
            }));
            break;

          case 'stepCounterUpdate':
            if (data.type === 'MIDNIGHT_RESET') {
              console.log('Service 자정 리셋 수신');
              setCurrentSteps(0);
              window.dispatchEvent(new CustomEvent('stepData', {
                detail: {
                  type: 'MIDNIGHT_RESET',
                  dateChanged: true,
                  triggerType: 'MIDNIGHT_RESET'
                }
              }));
            } else {
              const steps = data.steps ?? 0;
              setCurrentSteps(steps);
              setSensorType(data.sensorType || sensorType);
            }
            break;

          /****************  안드로이드 ******************/

          case 'STEP_SYNC_SUCCESS':
            setSyncStatus(prev => ({ ...prev, lastSyncTime: data.timestamp, hasPendingSync: false }));
            break;

          case 'STEP_SYNC_FAILED':
            console.log('❌ 동기화 실패:', data.error);
            break;

          case 'STEP_WEEKLY_DATA_SERVER':
            setWeeklyData(data.weeklyData);
            break;

          case 'STEP_SYNC_STATUS':
            setSyncStatus({
              lastSyncTime: data.lastSyncTime,
              hasPendingSync: data.hasPendingSync,
              pendingCount: data.pendingCount,
              isSyncing: false
            });
            break;

          case 'STEP_MANUAL_SYNC_COMPLETE':
            setSyncStatus(prev => ({ ...prev, isSyncing: false }));
            break;

          // STEP_COUNTER_PERMISSION_STATUS 케이스 수정
          case 'STEP_COUNTER_PERMISSION_STATUS':
            if (!env.isAndroid) break;
            console.log('📋 권한 상태 업데이트:', data);
            const wasNoPermission = !hasPermission;

            setHasPermission(data.granted);
            setPermissionLoading(false);
            setPermissionChecked(true);

            if (data.status === 'PERMISSION_DENIED') {
              setPermissionDenied(true); // ✅ 추가
              setError('걸음 수 측정 권한이 거부되었습니다.');
              shouldAutoStart.current = false;
            } else if (data.status === 'PERMISSION_ERROR') {
              setPermissionDenied(true); // ✅ 추가
              setError(`권한 요청 오류: ${data.error}`);
              shouldAutoStart.current = false;
            } else if (data.status === 'PERMISSION_GRANTED') {
              setPermissionDenied(false); // ✅ 추가
              setError(null);
              console.log('✅ 걸음 수 권한 획득 완료');

              if (wasNoPermission && shouldAutoStart.current) {
                console.log('🚀 권한 획득 완료 - 자동 추적 시작');
                permissionJustGranted.current = true;
                shouldAutoStart.current = false;

                setTimeout(() => {
                  sendToNative('STEP_START_TRACKING');
                }, 500);
              }
            }
            break;

          case 'STEP_PERMISSION_ERROR':
            console.error('❌ 권한 오류:', data.message);
            setError(data.message);
            setPermissionLoading(false);
            shouldAutoStart.current = false;
            break;

          case 'STEP_PERMISSION_REQUIRED':
            console.warn('⚠️ 권한 필요');
            setError('걸음 수 측정 권한이 필요합니다.');
            setHasPermission(false);
            break;
          // StepCounter 기능 메시지들
          case 'STEP_UPDATE':
            const steps = data.steps ?? data.data?.steps ?? 0;
            console.log('STEP_UPDATE 수신:', steps, 'source:', data.source);
            setCurrentSteps(steps);
            break;

          case 'STEP_TRACKING_STARTED':
            setIsTracking(true);
            setError(null);
            console.log('✅ 추적 시작됨');

            // 권한 방금 받아서 시작된 경우 초기 데이터 로드
            if (permissionJustGranted.current) {
              permissionJustGranted.current = false;
              setTimeout(() => {
                sendToNative('STEP_GET_CURRENT');
                sendToNative('STEP_GET_SENSOR_TYPE');
              }, 1000);
            }
            break;

          case 'STEP_TRACKING_STOPPED':
            setIsTracking(false);
            console.log('⏸️ 추적 중지됨');
            break;

          case 'STEP_CURRENT_STEPS':
            setCurrentSteps(data.steps);
            console.log('📊 현재 걸음 수:', data.steps);
            // alert(data.steps);
            break;

          case 'STEP_SENSOR_TYPE':
            setSensorType(data.sensorType);
            console.log('🔧 센서 타입:', data.sensorType);
            break;

          case 'STEP_SENSOR_AVAILABILITY':
            console.log('🔍 센서 가용성:', data);
            break;

          case 'STEP_ERROR':
            console.error('❌ StepCounter 오류:', data.message);
            setError(data.message);
            break;

          // 데이터 관련 메시지들
          case 'STEP_DAILY_DATA':
          case 'STEP_WEEKLY_DATA':
          case 'STEP_MONTHLY_DATA':
            window.dispatchEvent(new CustomEvent('stepData', {
              detail: { type, data: data[type.split('_')[1].toLowerCase() + 'Steps'] || data }
            }));
            break;

          // 하위 호환성을 위한 기존 메시지들
          case 'stepUpdate':
            setCurrentSteps(data.steps);
            break;

          case 'stepCounterStarted':
            setIsTracking(true);
            setError(null);
            break;

          case 'stepCounterStopped':
            setIsTracking(false);
            break;

          case 'currentSteps':
            setCurrentSteps(data.steps);
            break;

          case 'sensorType':
            setSensorType(data.sensorType);
            break;

          case 'error':
            setError(data.message);
            console.error('Native error:', data.message);
            break;

          default:
            if (type?.startsWith('STEP_')) {
              console.log('🤷‍♂️ 알 수 없는 StepCounter 메시지:', type, data);
            }
        }
      } catch (error) {
        // alert(`❌ 메시지 파싱 오류: ${error}`);
        console.error('❌ 메시지 파싱 오류:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [env, hasPermission, sendToNative]);

  // 컴포넌트 마운트 시 초기 설정
  useEffect(() => {
    if (env.isAndroid && !permissionChecked) {
      checkPermission();
    }
  }, [env.isAndroid, permissionChecked, checkPermission]);

  useEffect(() => {
    if (env.isAndroid && hasPermission && permissionChecked) {
      console.log('Android 권한 있음 - 현재 걸음 수 요청');

      setTimeout(() => {
        sendToNative('STEP_GET_CURRENT');

        if (!isTracking) {
          sendToNative('STEP_START_TRACKING');
        }
      }, 300);
    }
  }, [env.isAndroid, hasPermission, permissionChecked]);

  // 권한 획득 후 센서 가용성 확인
  useEffect(() => {
    if (hasPermission && !isTracking && permissionChecked) {
      console.log('🔍 권한 있음 - 센서 가용성 확인');
      checkSensorAvailability();
    }
  }, [hasPermission, isTracking, permissionChecked, checkSensorAvailability]);

  // 일별/주별/월별 데이터 가져오기 함수들
  const getDailySteps = useCallback((days = 7) => {
    if (hasPermission) {
      sendToNative('STEP_GET_DAILY', { days });
    }
  }, [sendToNative, hasPermission]);

  const getWeeklySteps = useCallback((weeks = 4) => {
    if (hasPermission) {
      sendToNative('STEP_GET_WEEKLY', { weeks });
    }
  }, [sendToNative, hasPermission]);

  const getMonthlySteps = useCallback((months = 6) => {
    if (hasPermission) {
      sendToNative('STEP_GET_MONTHLY', { months });
    }
  }, [sendToNative, hasPermission]);

  const resetTodaySteps = useCallback(() => {
    if (hasPermission) {
      sendToNative('STEP_RESET_TODAY');
    }
  }, [sendToNative, hasPermission]);

  return {
    // 상태
    currentSteps,
    isTracking,
    sensorType,
    isReady,
    error,

    setBaseSteps: useCallback((steps) => {
      if (env.isAndroid && hasPermission) {
        console.log('서버 기준 걸음수 설정 요청:', steps);
        sendToNative('STEP_SET_BASE_STEPS', { steps });
      }
    }, [env.isAndroid, hasPermission, sendToNative]),

    // 안드로이드
    ...(env.isAndroid ? {
      syncStatus,
      requestManualSync,
      requestWeeklyDataFromServer,
      checkSyncStatus,
    } : {}),

    // 🔐 권한 관련 상태
    hasPermission,
    permissionLoading,
    permissionChecked,
    permissionDenied,

    // 🔐 권한 관련 액션
    requestPermission,
    checkPermission,

    // StepCounter 액션들
    startTracking,
    stopTracking,
    getCurrentSteps,
    getSensorType,
    getDailySteps,
    getWeeklySteps,
    getMonthlySteps,
    resetTodaySteps,
    checkSensorAvailability
  };
};